class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.buttonObserver = null;
    this.footerObserver = null;
    this.buttonHidden = false;
    this.footerVisible = false;
    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
  }

  connectedCallback() {
    // Move element to body to escape section stacking context
    document.body.appendChild(this);

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.init());
    } else {
      this.init();
    }

    // Keep the sticky button's availability in sync with the selected variant.
    document.addEventListener("variant:updated", this.handleVariantUpdate);
  }

  init() {
    const originalButton = document.querySelector('.pdp-atc button[type="submit"]');
    // The footer group renders as #shopify-section-group-footer-group (its
    // group filename), not #shopify-section-group-footer — fall back to the
    // <footer> landmark itself so this keeps working even if the group is
    // renamed again.
    const footer =
      document.querySelector("#shopify-section-group-footer-group") || document.querySelector("footer");

    if (!originalButton) {
      return;
    }

    this.initOptionProxy();

    // Watch original ATC button
    this.buttonObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.buttonHidden = !entry.isIntersecting;
          this.updateVisibility();
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0,
      },
    );
    this.buttonObserver.observe(originalButton);

    // Watch footer — hide sticky bar when footer is visible
    if (footer) {
      this.footerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.footerVisible = entry.isIntersecting;
            this.updateVisibility();
          });
        },
        {
          root: null,
          rootMargin: "0px",
          threshold: 0,
        },
      );
      this.footerObserver.observe(footer);
    }
  }

  updateVisibility() {
    this.classList.toggle("visible", this.buttonHidden && !this.footerVisible);
  }

  // Wires the sticky bar's own option <select> elements (rendered in
  // snippets/sticky-add-to-cart.liquid) so they act as a proxy for the real
  // picker inside the in-page <variant-selector>. They never submit their
  // own value — changing one finds the matching control (radio or select)
  // for the same option index on the page and dispatches a `change` event on
  // it, letting variant-selector.js (URL, price, gallery, button state) run
  // exactly as it would from the in-page picker.
  initOptionProxy() {
    const selects = this.querySelectorAll("[data-sticky-option]");
    selects.forEach((select) => {
      select.addEventListener("change", () => this.proxyOptionChange(select));
    });
  }

  proxyOptionChange(select) {
    const index = Number(select.dataset.optionIndex);
    const value = select.value;
    const mainSelector = document.querySelector("variant-selector");
    const container = mainSelector?.querySelectorAll(".product-form__input")[index];

    if (container) {
      const radio = Array.from(container.querySelectorAll('input[type="radio"]')).find(
        (input) => input.value === value,
      );

      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event("change", { bubbles: true }));
      } else {
        const nativeSelect = container.querySelector("select");
        if (nativeSelect) {
          nativeSelect.value = value;
          nativeSelect.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
    }

    // QA: the closed control must always read the option name (e.g. "Size"),
    // never the value just picked — before AND after a selection. Snap it
    // back to its placeholder option right after proxying the change.
    select.value = "";
  }

  // Sync the sticky Add-to-Cart button's disabled state + label with the
  // newly selected variant. Driven by the `variant:updated` event that
  // variant-selector.js dispatches, mirroring how the main button updates.
  // (The sticky option <select>s are intentionally NOT synced to the
  // variant here — they always display their placeholder option name, never
  // the selected value; see proxyOptionChange.)
  handleVariantUpdate(event) {
    const variant = event.detail?.variant;
    if (!variant) return;

    const button = this.querySelector('button[type="submit"]');
    if (button) {
      button.disabled = !variant.available;

      const textEl = button.querySelector(".atc-button__text");
      if (textEl) {
        textEl.textContent = variant.available
          ? button.dataset.addLabel || textEl.textContent
          : button.dataset.soldOutLabel || textEl.textContent;
      }
    }
  }

  disconnectedCallback() {
    if (this.buttonObserver) {
      this.buttonObserver.disconnect();
    }
    if (this.footerObserver) {
      this.footerObserver.disconnect();
    }
    document.removeEventListener("variant:updated", this.handleVariantUpdate);
  }
}

customElements.define("sticky-add-to-cart", StickyAddToCart);
